import { useTranslations } from 'next-intl'
import styles from './step1.module.css'
import { TextField } from "@mui/material"
import { FormEvent, useEffect, useRef, useState } from 'react'
import { isEmail, isRequired } from '@/utils/validators'
import { getFieldError, toBase64URI, validateForm } from '@/utils/helpers'
import Button, { ButtonSize } from '@/components/UI/Buttons/Button/Button'
import { useRequestPasswordCode } from '@/apiUtilities/mutations/useRequestPasswordCode'
import { ForgotPasswordSteps } from '../page'
import TooltipField from '@/components/UI/Tooltips/TooltipField/TooltipField'
import { FieldState, ValidationsForForm } from '@/interfaces/api/forms/forms_types'
import { API_Error } from '@/interfaces/api/response/api_response_types'
import { toast } from 'react-toastify'
import Notification from '@/components/Notification/Notification'

interface ForgotPasswordStep1FormState {
	username: FieldState<string>
}

/**
 * Initial (empty) validations object for the login form.
 */
const initialValidations: ValidationsForForm<ForgotPasswordStep1FormState> = {
	username: []
}

interface ForgotPasswordStep1Props {
	setUsername: (username: string) => void,
	setStep: (step: ForgotPasswordSteps) => void
}

export default function ForgotPassword_Step1 ({ setUsername, setStep }: ForgotPasswordStep1Props) {
	const t = useTranslations('ForgotPassword')
	const requestPasswordCode = useRequestPasswordCode()
	const userNameRef = useRef<HTMLInputElement | null>(null)
	const [usernameFocused, setUsernameFocused] = useState<boolean>(false)
	const [isFormValid, setIsFormValid] = useState<boolean>(false)
	const [formState, setFormState] = useState<ForgotPasswordStep1FormState>({
		username: {
			value: '',
			dirty: false,
			required: true,
			validators: [
				{ validator: isRequired, show: false },
				{ validator: isEmail, show: false }
			]
		}
	})
	const [error, setError] = useState<boolean>(false)
	const [validations, setValidations] = useState<ValidationsForForm<ForgotPasswordStep1FormState>>(initialValidations)

	/**
   * Handles input changes for both username and password fields.
   * Marks the field as `dirty` and updates its `value`.
	 * 
   * @param e - Input change event from the TextField.
   */
	function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
		const key = e.target.name as keyof ForgotPasswordStep1FormState
		if (error) setError(false)
		setFormState(formStatePrev => {
			return {
				...formStatePrev,
				[key]: {
					...formStatePrev[key],
					value: e.target.value,
					dirty: true
				}
			}
		})
	}

	/**
	 * Handles successful retrieval of the verification code:
	 * - Updates the username in the parent component state (to be used in the next steps)
	 * - Advances to the next step of the forgot password flow
	 */
	async function handleSuccessRequestCode () {
    setUsername(formState.username.value)
		setStep(ForgotPasswordSteps.STEP_2)
		toast.success(<Notification text={t('messages.server.step1EmailSent')} />)
  }

	/**
	 * Handles get verification code API errors and updates UI state accordingly.
	 * 
	 * @param response - The API error response returned by the backend.
	 */
	async function handleErrorRequestCode (response: API_Error) {
		setError(true)
	}

	/**
	 * Handles form submission:
	 * - prevents the default browser submit
   * - triggers the request password code mutation
	 * 
	 * @param e - Form submit event
	 */
	function handleSubmit(e:FormEvent) {
		e.preventDefault()
		requestPasswordCode.mutate( toBase64URI(formState.username.value), {
			onSuccess: () => handleSuccessRequestCode(),
			onError: (response) => handleErrorRequestCode(response)
		})
	}

	/**
   * Re-validates the form whenever a field value changes.
   * Updates both:
   * - `validations` (to show messages in the tooltips)
   * - `formIsValid` (to enable/disable the submit button)
   */
	useEffect(() => {
		const { validations: newValidations, isValid } = validateForm(formState, initialValidations)
		setValidations(newValidations)
		setIsFormValid(isValid)
	}, [formState])

	/**
   * Focuses the username field if there's a server error (e.g., wrong credentials) to prompt the user to correct it.
   */
  useEffect(() => {
    if (error && userNameRef.current) userNameRef.current.focus()
  }, [error])

	const userNameError = getFieldError<ForgotPasswordStep1FormState>(formState, validations, 'username')

	return (
		<form className={styles.main} onSubmit={handleSubmit} data-testid='Step1'>
			<div className={styles.contentWrapper}>
				<p data-testid='Step1_description' className={`bodyTextMedium ${styles.description}`}>{t('step1Description')}</p>
				<TooltipField
					validations={validations.username}
					placement="bottom-start"
					arrow={false}
					disableHoverListener={true}
					disableTouchListener={true}
					hasHelperText={true}
				>
					<TextField
						data-testid='Step1_username'
						type="text"
						name="username"
						label={t('email')}
						value={formState.username.value}
						onChange={handleChange}
						error={error || (userNameError.hasError && !usernameFocused)}
						helperText={error 
												? t('messages.fields.step1PasswordResetRequestError') 
												: !usernameFocused && userNameError.msg !== undefined
													? t(`messages.fields.${userNameError.msg}`)
													: ' '
						}
						autoFocus={true}
						onFocus={() => setUsernameFocused(true)}
            onBlur={() => setUsernameFocused(false)}
						inputRef={userNameRef}
					/>
				</TooltipField>
			</div>

			<Button data-testid='Step1_button' type={"submit"} size={ButtonSize.LARGE} disabled={!isFormValid} className={styles.submitButton}>{t('nextButton')}</Button>
		</form>
	)
}